import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mic, Square, RotateCcw, ChevronRight, ChevronLeft, Bookmark, Sparkles, Timer, Volume2, AlertTriangle, Lock, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const scoreLabels = ["Poor", "Fair", "Good", "Strong", "Excellent"];

function useSpeechRecognition() {
  const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const supported = !!SR;
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [finalText, setFinalText] = useState("");
  const [error, setError] = useState(null);

  const start = () => {
    if (!SR) { setError("This browser does not support Web Speech. Try Chrome or Edge."); return; }
    setError(null);
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    let localFinal = "";
    rec.onresult = (e) => {
      let iv = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) localFinal += (localFinal ? " " : "") + t.trim();
        else iv += t;
      }
      setInterim(iv);
      if (localFinal) setFinalText(localFinal);
    };
    rec.onerror = (e) => { setError(e.error || "Speech recognition error"); setListening(false); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setFinalText("");
    setInterim("");
    setListening(true);
    rec.start();
  };
  const stop = () => {
    try { recRef.current?.stop(); } catch (_) {}
    setListening(false);
  };
  const reset = () => { setFinalText(""); setInterim(""); setError(null); };
  useEffect(() => () => { try { recRef.current?.stop(); } catch (_) {} }, []);

  return { supported, listening, interim, finalText, error, start, stop, reset, setFinalText };
}

const scoreAnswer = (text, keyPoints = []) => {
  const words = (text || "").trim().split(/\s+/).filter(Boolean);
  const wc = words.length;
  const lc = text.toLowerCase();
  const hits = keyPoints.filter(k => {
    const kw = k.toLowerCase().split(/\s+/).slice(0, 2).join(" ");
    return lc.includes(kw);
  }).length;
  const coverage = keyPoints.length ? hits / keyPoints.length : 0;
  // Rough heuristics 0-5
  const completeness = Math.max(0, Math.min(5, Math.round(coverage * 5)));
  const technical = Math.max(0, Math.min(5, Math.round(coverage * 4 + (wc > 60 ? 1 : 0))));
  const communication = wc < 20 ? 1 : wc < 50 ? 2 : wc < 100 ? 4 : wc < 200 ? 5 : 4; // sweet spot ~100-200 words
  const confidence = wc < 20 ? 1 : wc < 80 ? 3 : 5;
  return { technical, communication, confidence, completeness, wordCount: wc, keyHits: hits };
};

export default function InterviewSimulator() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState("hse-officer");
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [showModel, setShowModel] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);
  const speech = useSpeechRecognition();

  useEffect(() => { api.get("/interview/roles").then(r => setRoles(r.data.roles)); }, []);
  useEffect(() => {
    api.get("/interview/questions", { params: { role } }).then(r => {
      setQs(r.data.questions.filter(q => !q.locked));
      setIdx(0); setShowModel(false); speech.reset(); setSeconds(0); setRunning(false);
    });
    // eslint-disable-next-line
  }, [role]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const q = qs[idx];
  const text = (speech.finalText + (speech.interim ? " " + speech.interim : "")).trim();
  const scores = useMemo(() => q ? scoreAnswer(text, q.key_points) : null, [text, q]);

  const start = () => { speech.reset(); setSeconds(0); setRunning(true); setShowModel(false); speech.start(); };
  const stop = () => { speech.stop(); setRunning(false); };
  const nextQ = () => {
    stop(); speech.reset(); setShowModel(false); setSeconds(0); setManualEdit(false);
    setIdx(i => Math.min(qs.length - 1, i + 1));
  };
  const prevQ = () => {
    stop(); speech.reset(); setShowModel(false); setSeconds(0); setManualEdit(false);
    setIdx(i => Math.max(0, i - 1));
  };
  const bookmark = async () => {
    if (!q) return;
    try {
      await api.post("/bookmarks", { kind: "interview", ref_id: q.iq_id, title: q.text });
      toast.success("Question saved to bookmarks");
    } catch (e) { toast.error("Please sign in"); }
  };
  const speakQuestion = () => {
    if (!q || !("speechSynthesis" in window)) return;
    const utter = new SpeechSynthesisUtterance(q.text);
    utter.rate = 0.95;
    window.speechSynthesis.speak(utter);
  };

  if (!user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto rounded-2xl border border-amber-500/40 bg-amber-500/10 p-8 text-center" data-testid="simulator-locked">
        <Lock className="h-8 w-8 text-amber-500 mx-auto" />
        <h1 className="mt-3 text-2xl font-bold">Interview Simulator — Premium</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">Rehearse spoken answers with speech-to-text scoring across HSE role questions.</p>
        <Link to="/subscription" data-testid="simulator-upgrade-btn" className="btn-accent mt-4">Upgrade to Premium</Link>
      </div>
    );
  }

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="space-y-5" data-testid="interview-simulator-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="pill bg-amber-500/15 text-amber-600 dark:text-amber-400"><Sparkles className="h-3 w-3" /> Premium simulator</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Interview Simulator</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Speak your answer out loud. We'll transcribe and score it.</p>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {roles.map(r => (
            <button key={r.slug} data-testid={`sim-role-${r.slug}`} onClick={()=>setRole(r.slug)}
                    className={`pill whitespace-nowrap ${role === r.slug ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>{r.name}</button>
          ))}
        </div>
      </div>

      {!speech.supported && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold">Speech recognition unavailable in this browser.</div>
            <div className="text-slate-600 dark:text-slate-400">Use Chrome or Edge for the best experience. You can still type your answer below to get feedback.</div>
          </div>
        </div>
      )}

      {q ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Question {idx + 1} of {qs.length} · {q.category}</div>
                  <h2 className="mt-2 text-xl sm:text-2xl font-bold leading-snug">{q.text}</h2>
                </div>
                <div className="flex gap-1">
                  <button data-testid="sim-speak-btn" onClick={speakQuestion} title="Play question" className="p-2 rounded-full border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800"><Volume2 className="h-4 w-4" /></button>
                  <button data-testid="sim-bookmark-btn" onClick={bookmark} title="Save" className="p-2 rounded-full border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800"><Bookmark className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Mic control */}
              <div className="mt-6 flex flex-col items-center">
                <button
                  data-testid={running ? "sim-stop-btn" : "sim-start-btn"}
                  onClick={running ? stop : start}
                  disabled={!speech.supported}
                  className={`h-24 w-24 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 ${running ? "bg-rose-500 text-white animate-pulse" : "bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-950"} disabled:opacity-40`}>
                  {running ? <Square className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
                </button>
                <div className="mt-3 flex items-center gap-3">
                  <span className="mono text-lg font-bold flex items-center gap-1" data-testid="sim-timer"><Timer className="h-4 w-4" />{mm}:{ss}</span>
                  <button data-testid="sim-reset-btn" onClick={() => { stop(); speech.reset(); setSeconds(0); setShowModel(false); setManualEdit(false); }} className="btn-outline !py-1.5 !px-3 text-xs"><RotateCcw className="h-3 w-3" /> Reset</button>
                </div>
                <div className="text-xs text-slate-500 mt-2">{running ? "Listening… speak your answer clearly." : speech.supported ? "Tap the mic to start speaking." : "Type your answer below."}</div>
                {speech.error && <div className="mt-2 text-xs text-rose-500">{speech.error}</div>}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-500">Your answer</div>
                <button data-testid="sim-edit-btn" onClick={() => setManualEdit(m => !m)} className="text-xs font-semibold text-amber-600">{manualEdit ? "Done" : "Edit"}</button>
              </div>
              {manualEdit ? (
                <Textarea data-testid="sim-answer-editor" rows={6} value={text} onChange={(e) => speech.setFinalText(e.target.value)} placeholder="Type your answer here…" />
              ) : (
                <div data-testid="sim-transcript" className="min-h-[100px] whitespace-pre-wrap text-sm leading-relaxed">
                  {text ? (
                    <>
                      <span>{speech.finalText}</span>
                      {speech.interim && <span className="text-slate-400"> {speech.interim}</span>}
                    </>
                  ) : (
                    <span className="text-slate-400">Your transcript will appear here as you speak.</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-2">
                <button data-testid="sim-prev-btn" disabled={idx === 0} onClick={prevQ} className="btn-outline disabled:opacity-40 !py-2 !px-4 text-sm"><ChevronLeft className="h-4 w-4" /> Prev</button>
                <button data-testid="sim-next-btn" disabled={idx === qs.length - 1} onClick={nextQ} className="btn-primary disabled:opacity-40 !py-2 !px-4 text-sm">Next <ChevronRight className="h-4 w-4" /></button>
              </div>
              <button data-testid="sim-reveal-btn" onClick={() => setShowModel(s => !s)} className="btn-accent !py-2 !px-4 text-sm">{showModel ? "Hide model answer" : "Show model answer"}</button>
            </div>

            {showModel && (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5 space-y-3 text-sm" data-testid="sim-model-answer">
                <div><div className="text-xs uppercase tracking-widest text-emerald-600">Professional answer</div><p className="mt-1">{q.professional_answer}</p></div>
                <div><div className="text-xs uppercase tracking-widest text-emerald-600">Practical example</div><p className="mt-1">{q.example}</p></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-600">Key points</div>
                  <ul className="mt-1 flex flex-wrap gap-1">{q.key_points?.map((k,i)=>(<li key={i} className={`pill ${text.toLowerCase().includes(k.toLowerCase().split(" ").slice(0,2).join(" ")) ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" : "bg-slate-100 dark:bg-slate-800"}`}>{text.toLowerCase().includes(k.toLowerCase().split(" ").slice(0,2).join(" ")) && <CheckCircle2 className="h-3 w-3" />}{k}</li>))}</ul>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-3">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500">Live scoring</div>
              <p className="text-xs text-slate-500 mt-1">Automatic estimate based on length and key-point coverage.</p>
              <div className="mt-4 space-y-3">
                {[
                  ["Technical", scores?.technical || 0, "sim-score-technical"],
                  ["Communication", scores?.communication || 0, "sim-score-communication"],
                  ["Confidence", scores?.confidence || 0, "sim-score-confidence"],
                  ["Completeness", scores?.completeness || 0, "sim-score-completeness"],
                ].map(([label, val, tid]) => (
                  <div key={label} data-testid={tid}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold">{label}</span>
                      <span className="mono">{val}/5 · {scoreLabels[val] || "—"}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full ${val >= 4 ? "bg-emerald-500" : val >= 2 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${(val/5)*100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-slate-500">Words</div><div className="mono font-bold">{scores?.wordCount || 0}</div></div>
                <div><div className="text-slate-500">Key points hit</div><div className="mono font-bold">{scores?.keyHits || 0}/{q.key_points?.length || 0}</div></div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-xs text-slate-500">
              <div className="font-semibold text-slate-700 dark:text-slate-300 text-sm mb-1">Tips</div>
              <ul className="list-disc pl-4 space-y-1">
                <li>Aim for 90–180 seconds per answer.</li>
                <li>Use STAR: Situation, Task, Action, Result.</li>
                <li>Mention 2–3 key points from the sidebar.</li>
                <li>Speak clearly and pause between ideas.</li>
              </ul>
            </div>
          </aside>
        </div>
      ) : (
        <div className="text-slate-500 text-sm">Loading questions…</div>
      )}

      <div className="text-xs text-slate-500">
        Prefer text prep? <Link to="/tools/interview" className="font-semibold text-amber-600">Open the full interview question bank →</Link>
      </div>
    </div>
  );
}
